"use client";

import { useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  Panel,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseScientificInput } from "@/lib/tool-kit/earth-science/format";
import {
  fromMetres,
  groundAreaFromMap,
  groundFromMap,
  type LengthUnit,
  mapFromGround,
  parseScale,
  readableUnit,
  scaleFromDistances,
  toMetres,
  type UnitSystem,
} from "@/lib/tool-kit/earth-science/map-scale";

/** English strings. A translated content file overrides these through `ui`. */
export const MAP_SCALE_CALCULATOR_STRINGS = {
  modeLabel: "What do you want to find?",
  modeGround: "Real distance from a map distance",
  modeMap: "Map distance from a real distance",
  modeScale: "The scale from two distances",
  scaleLabel: "Map scale",
  scaleHint:
    "Type 1:50000, 1:24,000 or just 50000. Two numbers a:b mean 1 unit on the map is b ÷ a on the ground.",
  mapLabel: "Distance on the map",
  groundLabel: "Real distance on the ground",
  unitLabel: "Unit",
  outputUnitLabel: "Show the answer in",
  units: {
    mm: "mm",
    cm: "cm",
    m: "m",
    km: "km",
    in: "in",
    ft: "ft",
    yd: "yd",
    mi: "mi",
    nmi: "nmi",
  },
  results: "Results",
  resultGround: "Real distance",
  resultMap: "Distance on the map",
  resultScale: "Representative fraction",
  scaleText: "1:{n}",
  verbal: "Verbal scale",
  verbalText: "1 {mapUnit} on the map = {value} {groundUnit} on the ground",
  areaHeading: "Area",
  areaText: "1 cm² on the map = {value} {unit} on the ground",
  areaHa: "ha",
  areaKm2: "km²",
  areaM2: "m²",
  sizeNote:
    "A bigger N is a smaller-scale map: it shows more ground in less detail.",
  workingHeading: "The working",
  stepGround:
    "{map} {mapUnit} = {metres} m on the map; × {n} = {groundMetres} m = {answer} {unit} on the ground.",
  stepMap:
    "{ground} {groundUnit} = {metres} m on the ground; ÷ {n} = {mapMetres} m = {answer} {unit} on the map.",
  stepScale:
    "{ground} on the ground ÷ {map} on the map, in the same unit: {groundMetres} m ÷ {mapMetres} m = {n}, so the scale is 1:{n}.",
  tableMeansHeading: "What one map length means on the ground",
  columnMap: "On the map",
  columnMetric: "On the ground (metric)",
  columnImperial: "On the ground (imperial)",
  tableRulerHeading: "What one ground distance looks like on the map",
  columnGround: "On the ground",
  columnCentimetres: "On the map (cm)",
  columnInches: "On the map (in)",
  copyResults: "Copy results",
  copied: "Copied!",
  copyLine: "Scale 1:{n}: {map} on the map = {ground} on the ground",
  enterValues: "Enter the values above to see the result.",
  scaleInvalid:
    "The scale could not be read. Type it as 1:50000, 1/24000 or just the number 50000, with a value above zero.",
  distanceInvalid: "{name} must be a number greater than zero.",
} as const;

type Strings = typeof MAP_SCALE_CALCULATOR_STRINGS;
type Fmt = (value: number, options?: Intl.NumberFormatOptions) => string;
type Mode = "ground" | "map" | "scale";

const UNITS: LengthUnit[] = [
  "mm",
  "cm",
  "m",
  "km",
  "in",
  "ft",
  "yd",
  "mi",
  "nmi",
];
const SIG = { maximumSignificantDigits: 4 };
const WORK = { maximumSignificantDigits: 7 };
const MAP_LENGTHS: [number, LengthUnit][] = [
  [1, "mm"],
  [1, "cm"],
  [10, "cm"],
  [1, "in"],
];
const GROUND_DISTANCES: [number, LengthUnit][] = [
  [100, "m"],
  [1, "km"],
  [5, "km"],
  [1, "mi"],
];

function amount(fmt: Fmt, metres: number, unit: LengthUnit, strings: Strings) {
  return `${fmt(fromMetres(metres, unit), SIG)} ${strings.units[unit]}`;
}

function bestAmount(
  fmt: Fmt,
  metres: number,
  system: UnitSystem,
  strings: Strings
): string {
  return amount(fmt, metres, readableUnit(metres, system), strings);
}

function ScaleTables({
  n,
  strings,
  fmt,
}: {
  n: number;
  strings: Strings;
  fmt: Fmt;
}) {
  return (
    <>
      <h3 className={WIDGET_H3}>{strings.tableMeansHeading}</h3>
      <DataTable
        head={[strings.columnMap, strings.columnMetric, strings.columnImperial]}
        rows={MAP_LENGTHS.map(([value, unit]) => {
          const ground = groundFromMap(toMetres(value, unit), n);
          return [
            `${fmt(value)} ${strings.units[unit]}`,
            bestAmount(fmt, ground, "metric", strings),
            bestAmount(fmt, ground, "imperial", strings),
          ];
        })}
      />
      <h3 className={WIDGET_H3}>{strings.tableRulerHeading}</h3>
      <DataTable
        head={[
          strings.columnGround,
          strings.columnCentimetres,
          strings.columnInches,
        ]}
        rows={GROUND_DISTANCES.map(([value, unit]) => {
          const map = mapFromGround(toMetres(value, unit), n);
          return [
            `${fmt(value)} ${strings.units[unit]}`,
            fmt(fromMetres(map, "cm"), SIG),
            fmt(fromMetres(map, "in"), SIG),
          ];
        })}
      />
    </>
  );
}

function areaText(strings: Strings, fmt: Fmt, n: number): string {
  const metres = groundAreaFromMap(0.0001, n);
  let unit: string = strings.areaM2;
  let value = metres;
  if (metres >= 1_000_000) {
    unit = strings.areaKm2;
    value = metres / 1_000_000;
  } else if (metres >= 10_000) {
    unit = strings.areaHa;
    value = metres / 10_000;
  }
  return t(strings.areaText, { value: fmt(value, SIG), unit });
}

interface Inputs {
  scale: string;
  map: string;
  mapUnit: LengthUnit;
  ground: string;
  groundUnit: LengthUnit;
  outUnit: LengthUnit;
}

interface Solved {
  n: number;
  headline: { label: string; value: string };
  working: string;
  copy: string;
}

type Problem = "empty" | "scale" | "map" | "ground";

function number(text: string): number | null {
  const value = parseScientificInput(text);
  return value !== null && value > 0 ? value : null;
}

/** Why a typed distance is unusable: nothing typed yet, or typed but not above zero. */
function distanceProblem(text: string, kind: "map" | "ground"): Problem {
  return text.trim() === "" ? "empty" : kind;
}

function solveGround(
  inputs: Inputs,
  n: number,
  strings: Strings,
  fmt: Fmt
): Solved | Problem {
  const map = number(inputs.map);
  if (map === null) {
    return distanceProblem(inputs.map, "map");
  }
  const mapM = toMetres(map, inputs.mapUnit);
  const groundM = groundFromMap(mapM, n);
  const answer = amount(fmt, groundM, inputs.outUnit, strings);
  return {
    n,
    headline: { label: strings.resultGround, value: answer },
    working: t(strings.stepGround, {
      map: fmt(map, SIG),
      mapUnit: strings.units[inputs.mapUnit],
      metres: fmt(mapM, WORK),
      n: fmt(n),
      groundMetres: fmt(groundM, WORK),
      answer: fmt(fromMetres(groundM, inputs.outUnit), SIG),
      unit: strings.units[inputs.outUnit],
    }),
    copy: t(strings.copyLine, {
      n: fmt(n),
      map: `${fmt(map, SIG)} ${strings.units[inputs.mapUnit]}`,
      ground: answer,
    }),
  };
}

function solveMap(
  inputs: Inputs,
  n: number,
  strings: Strings,
  fmt: Fmt
): Solved | Problem {
  const ground = number(inputs.ground);
  if (ground === null) {
    return distanceProblem(inputs.ground, "ground");
  }
  const groundM = toMetres(ground, inputs.groundUnit);
  const mapM = mapFromGround(groundM, n);
  const answer = amount(fmt, mapM, inputs.outUnit, strings);
  return {
    n,
    headline: { label: strings.resultMap, value: answer },
    working: t(strings.stepMap, {
      ground: fmt(ground, SIG),
      groundUnit: strings.units[inputs.groundUnit],
      metres: fmt(groundM, WORK),
      n: fmt(n),
      mapMetres: fmt(mapM, WORK),
      answer: fmt(fromMetres(mapM, inputs.outUnit), SIG),
      unit: strings.units[inputs.outUnit],
    }),
    copy: t(strings.copyLine, {
      n: fmt(n),
      map: answer,
      ground: `${fmt(ground, SIG)} ${strings.units[inputs.groundUnit]}`,
    }),
  };
}

function solveScale(
  inputs: Inputs,
  strings: Strings,
  fmt: Fmt
): Solved | Problem {
  const map = number(inputs.map);
  const ground = number(inputs.ground);
  if (map === null) {
    return distanceProblem(inputs.map, "map");
  }
  if (ground === null) {
    return distanceProblem(inputs.ground, "ground");
  }
  const mapM = toMetres(map, inputs.mapUnit);
  const groundM = toMetres(ground, inputs.groundUnit);
  const n = scaleFromDistances(mapM, groundM) ?? 1;
  const mapText = `${fmt(map, SIG)} ${strings.units[inputs.mapUnit]}`;
  const groundText = `${fmt(ground, SIG)} ${strings.units[inputs.groundUnit]}`;
  return {
    n,
    headline: {
      label: strings.resultScale,
      value: t(strings.scaleText, { n: fmt(n, SIG) }),
    },
    working: t(strings.stepScale, {
      ground: groundText,
      map: mapText,
      groundMetres: fmt(groundM, WORK),
      mapMetres: fmt(mapM, WORK),
      n: fmt(n, WORK),
    }),
    copy: t(strings.copyLine, {
      n: fmt(n, SIG),
      map: mapText,
      ground: groundText,
    }),
  };
}

function solve(
  mode: Mode,
  inputs: Inputs,
  strings: Strings,
  fmt: Fmt
): Solved | Problem {
  if (mode === "scale") {
    return solveScale(inputs, strings, fmt);
  }
  if (inputs.scale.trim() === "") {
    return "empty";
  }
  const n = parseScale(inputs.scale);
  if (n === null) {
    return "scale";
  }
  return mode === "ground"
    ? solveGround(inputs, n, strings, fmt)
    : solveMap(inputs, n, strings, fmt);
}

function Outcome({
  mode,
  inputs,
  strings,
  fmt,
}: {
  mode: Mode;
  inputs: Inputs;
  strings: Strings;
  fmt: Fmt;
}) {
  const solved = solve(mode, inputs, strings, fmt);
  if (solved === "empty") {
    return <Notice>{strings.enterValues}</Notice>;
  }
  if (solved === "scale") {
    return <ErrorNotice>{strings.scaleInvalid}</ErrorNotice>;
  }
  if (typeof solved === "string") {
    return (
      <ErrorNotice>
        {t(strings.distanceInvalid, {
          name: solved === "map" ? strings.mapLabel : strings.groundLabel,
        })}
      </ErrorNotice>
    );
  }
  const centimetre = groundFromMap(0.01, solved.n);
  const verbalUnit = readableUnit(centimetre, "metric");
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={solved.headline.label}
          value={solved.headline.value}
        />
        {mode === "scale" ? null : (
          <StatCard
            label={strings.resultScale}
            value={t(strings.scaleText, { n: fmt(solved.n, SIG) })}
          />
        )}
        <StatCard
          label={strings.verbal}
          value={t(strings.verbalText, {
            mapUnit: strings.units.cm,
            value: fmt(fromMetres(centimetre, verbalUnit), SIG),
            groundUnit: strings.units[verbalUnit],
          })}
        />
      </StatGrid>
      <p className="text-foreground text-sm">
        {areaText(strings, fmt, solved.n)}
      </p>
      <p className={HINT}>{strings.sizeNote}</p>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResults}
        small
        text={solved.copy}
      />
      <Panel title={strings.workingHeading}>
        <p className="text-foreground text-sm">{solved.working}</p>
      </Panel>
      <ScaleTables fmt={fmt} n={solved.n} strings={strings} />
    </>
  );
}

function UnitSelect({
  id,
  label,
  value,
  onChange,
  strings,
}: {
  id: string;
  label: string;
  value: LengthUnit;
  onChange: (unit: LengthUnit) => void;
  strings: Strings;
}) {
  return (
    <SelectField
      id={id}
      label={label}
      onChange={onChange}
      options={UNITS.map((unit) => ({
        value: unit,
        label: strings.units[unit],
      }))}
      value={value}
    />
  );
}

export default function MapScaleCalculatorWidget() {
  const strings = useToolUi(MAP_SCALE_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("ground");
  const [inputs, setInputs] = useState<Inputs>({
    scale: "1:50000",
    map: "3.4",
    mapUnit: "cm",
    ground: "2",
    groundUnit: "km",
    outUnit: "km",
  });
  const set = (patch: Partial<Inputs>) => setInputs({ ...inputs, ...patch });
  const showScale = mode !== "scale";
  const showMap = mode !== "map";
  const showGround = mode !== "ground";

  function switchMode(next: Mode) {
    setMode(next);
    set({ outUnit: next === "map" ? "cm" : "km" });
  }

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.modeLabel}
        name="msc-mode"
        onChange={switchMode}
        options={[
          { value: "ground", label: strings.modeGround },
          { value: "map", label: strings.modeMap },
          { value: "scale", label: strings.modeScale },
        ]}
        value={mode}
      />
      <div className="grid gap-4 sm:grid-cols-3">
        {showScale ? (
          <TextField
            hint={strings.scaleHint}
            id="msc-scale"
            label={strings.scaleLabel}
            onChange={(scale) => set({ scale })}
            value={inputs.scale}
          />
        ) : null}
        {showMap ? (
          <>
            <TextField
              id="msc-map"
              label={strings.mapLabel}
              numeric
              onChange={(map) => set({ map })}
              value={inputs.map}
            />
            <UnitSelect
              id="msc-map-unit"
              label={strings.unitLabel}
              onChange={(mapUnit) => set({ mapUnit })}
              strings={strings}
              value={inputs.mapUnit}
            />
          </>
        ) : null}
        {showGround ? (
          <>
            <TextField
              id="msc-ground"
              label={strings.groundLabel}
              numeric
              onChange={(ground) => set({ ground })}
              value={inputs.ground}
            />
            <UnitSelect
              id="msc-ground-unit"
              label={strings.unitLabel}
              onChange={(groundUnit) => set({ groundUnit })}
              strings={strings}
              value={inputs.groundUnit}
            />
          </>
        ) : null}
        {showScale ? (
          <UnitSelect
            id="msc-out-unit"
            label={strings.outputUnitLabel}
            onChange={(outUnit) => set({ outUnit })}
            strings={strings}
            value={inputs.outUnit}
          />
        ) : null}
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <Outcome fmt={fmt} inputs={inputs} mode={mode} strings={strings} />
      </section>
    </div>
  );
}
